import React, { useState } from 'react';
import { Sparkles, Loader2, Copy, Check } from 'lucide-react';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';

const scoreColor = (n) => {
  if (n >= 75) return 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30';
  if (n >= 50) return 'text-amber-200 bg-amber-500/10 border-amber-500/30';
  return 'text-slate-300 bg-white/5 border-white/15';
};

const AiLeadInsights = ({ kind, payload, testIdPrefix = 'ai' }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post('/ai/leads/analyze', { kind, payload });
      setData(res.data);
    } catch (e) {
      toast({ title: 'AI analysis failed', description: e?.response?.data?.detail || 'Try again.' });
    } finally {
      setLoading(false);
    }
  };

  const copyReply = async () => {
    if (!data?.suggested_reply) return;
    await navigator.clipboard.writeText(data.suggested_reply);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!data) {
    return (
      <button
        onClick={run}
        disabled={loading}
        data-testid={`${testIdPrefix}-insights-btn`}
        className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-500/5 text-amber-200 border border-amber-500/30 hover:from-amber-500/30 disabled:opacity-60 transition-colors"
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
        {loading ? 'Analyzing…' : 'AI Insights'}
      </button>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.06] to-transparent p-4" data-testid={`${testIdPrefix}-insights-panel`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-amber-200">
          <Sparkles size={14} />
          <span className="text-xs tracking-widest">AI INSIGHTS</span>
        </div>
        <div className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${scoreColor(data.score || 0)}`}>
          Score {data.score ?? '—'}/100
        </div>
      </div>

      <p className="text-slate-200 text-sm leading-relaxed">{data.summary}</p>

      {data.score_rationale && (
        <p className="text-slate-500 text-xs mt-1 italic">Why: {data.score_rationale}</p>
      )}

      {Array.isArray(data.signals) && data.signals.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {data.signals.map((s, i) => (
            <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-300">
              {s}
            </span>
          ))}
        </div>
      )}

      {data.suggested_reply && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] tracking-widest text-slate-500">SUGGESTED REPLY</div>
            <button
              onClick={copyReply}
              className="text-xs text-amber-200 hover:text-amber-100 inline-flex items-center gap-1"
              data-testid={`${testIdPrefix}-copy-reply`}
            >
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
          <div className="text-slate-200 text-sm whitespace-pre-wrap leading-relaxed bg-black/20 rounded-xl p-3 border border-white/5">
            {data.suggested_reply}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiLeadInsights;
